import { useRef, useState, useCallback, useEffect } from 'react';
import config from '../../../config';
import { getLocalAccessToken } from 'modules/user/userCookies';
import {
  getAccessTokenRefreshDelay,
  performRefresh,
} from 'modules/user/silentRefresh';
import { useQueryClient } from '@tanstack/react-query';
import {
  waitUntilTokenIsFresh,
  withTimeout,
} from '../../../routes/tokenRefresh';
import { LOCAL_STORAGE_FORCED_REFRESH } from '../../../app/dex/side-panels/SnipesPanel';

export interface Subscription {
  channel: string;
  data: any;
  auth?: boolean;
}

interface WebSocketServerReturn {
  subscribe: (subscription: Subscription) => void;
  unsubscribe: (subscription: Subscription) => void;
  addGlobalMessageListener: (callback: (msg: any) => void) => void;
  removeGlobalMessageListener: (callback: (msg: any) => void) => void;
  isConnected: boolean | undefined;
  isConnecting: boolean;
  getSocket?: () => WebSocket | null;
  connect: () => void;
  disconnect: () => void;
  waitForSocketOpen: () => Promise<void>;
}

const WEBSOCKET_URL = config.WEBSOCKET_URL;

const getSessionToken = () => {
  const existingToken = localStorage.getItem('websocketSessionToken');
  if (existingToken) return existingToken;

  const randomToken = crypto.randomUUID();
  localStorage.setItem('websocketSessionToken', randomToken);
  return randomToken;
};

const MAX_RECONNECT_ATTEMPTS = 5;
const RECONNECT_TIMEOUT_MS = 20000; // 20 seconds

export const useWebSocketServer = (): WebSocketServerReturn => {
  const [isConnected, setIsConnected] = useState<boolean | undefined>(
    undefined
  );
  const [isConnecting, setIsConnecting] = useState(false);
  const queryClient = useQueryClient();
  const reconnectAttemptsRef = useRef(0);

  const socketRef = useRef<WebSocket | null>(null);

  const [disconnectedDueToSleep, setDisconnectedDueToSleep] = useState(false);

  const globalListeners = useRef<((msg: any) => void)[]>([]);

  const sendMessage = (message: any) => {
    if (socketRef.current?.readyState === WebSocket.OPEN) {
      socketRef.current.send(JSON.stringify(message));
    } else {
      console.warn('⚠️ WebSocket not connected. Message not sent:', message);
    }
  };

  const connectWebSocket = useCallback(() => {
    if (
      socketRef.current &&
      socketRef.current.readyState !== WebSocket.CLOSED
    ) {
      socketRef.current.close();
    }

    const socket = new WebSocket(`${WEBSOCKET_URL}?token=${getSessionToken()}`);
    socketRef.current = socket;

    setIsConnecting(true);

    socket.onopen = () => {
      setIsConnected(true);
      setIsConnecting(false);
    };

    socket.onclose = (event) => {
      setIsConnected(false);
      setIsConnecting(false);
      if (event.code === 1006) {
        console.warn('WebSocket closed unexpectedly — possibly system sleep?');
        setDisconnectedDueToSleep(true);
      }
    };

    socket.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data);
        const isGlobalMessage = !message.channel && message.type;
        if (isGlobalMessage) {
          globalListeners.current.forEach((fn) => fn(message));
        }
      } catch (err) {
        console.error('❌ Failed to parse WebSocket message:', err);
      }
    };

    socket.onerror = (error) => {
      console.error('❌ WebSocket Error:', error);
      if (socket.readyState !== WebSocket.CLOSED) {
        socket.close();
      }
    };
  }, []);

  const subscribe = (subscription: Subscription) => {
    sendMessage({
      action: 'subscribe',
      channel: subscription.channel,
      data: subscription.data,
    });
  };

  const unsubscribe = (subscription: Subscription) => {
    sendMessage({
      action: 'unsubscribe',
      channel: subscription.channel,
      data: subscription.data,
    });
  };

  const waitForSocketOpen = (): Promise<void> => {
    return new Promise((resolve) => {
      const socket = socketRef.current;
      if (socket?.readyState === WebSocket.OPEN) {
        return resolve();
      }
      const interval = setInterval(() => {
        if (socketRef.current?.readyState === WebSocket.OPEN) {
          clearInterval(interval);
          resolve();
        }
      }, 50);
    });
  };

  useEffect(() => {
    if (!disconnectedDueToSleep) return;

    if (reconnectAttemptsRef.current > MAX_RECONNECT_ATTEMPTS) {
      return;
    }

    reconnectAttemptsRef.current += 1;

    // Async function to recover connection
    const recoverFromSleep = async () => {
      setDisconnectedDueToSleep(false);

      const token = getLocalAccessToken();
      const shouldRefresh = !token || getAccessTokenRefreshDelay(token, 5) <= 0;

      if (shouldRefresh) {
        try {
          await withTimeout(
            performRefresh({ isNotSilentRefresh: true }),
            RECONNECT_TIMEOUT_MS
          );
        } catch (err) {
          console.error('Token refresh failed during sleep recovery:', err);
        }
      }

      connectWebSocket();
      try {
        await withTimeout(waitForSocketOpen(), RECONNECT_TIMEOUT_MS);
      } catch (err) {
        console.error(
          'Failed to invalidate queries after sleep recovery:',
          err
        );
      }

      reconnectAttemptsRef.current = 0;

      const latestToken = getLocalAccessToken();
      const socket = socketRef.current;

      if (latestToken && socket instanceof WebSocket) {
        socket.send(
          JSON.stringify({
            action: 'authenticate',
            channel: 'auth',
            data: { token: latestToken },
          })
        );
      } else {
        console.warn('❌ No token available for WebSocket auth after sleep');
      }

      try {
        await withTimeout(waitUntilTokenIsFresh(), RECONNECT_TIMEOUT_MS);
        await withTimeout(queryClient.invalidateQueries(), 3000);
        window.localStorage.setItem(LOCAL_STORAGE_FORCED_REFRESH, 'true');
      } catch (err) {
        console.error(
          'Failed to invalidate queries after sleep recovery:',
          err
        );
      }
    };

    recoverFromSleep().catch((err) => {
      console.error('Error during sleep recovery:', err);
    });
  }, [
    connectWebSocket,
    waitForSocketOpen,
    queryClient,
    disconnectedDueToSleep,
  ]);

  const addGlobalMessageListener = (callback: (msg: any) => void) => {
    globalListeners.current.push(callback);
  };

  const removeGlobalMessageListener = (callback: (msg: any) => void) => {
    globalListeners.current = globalListeners.current.filter(
      (fn) => fn !== callback
    );
  };

  const disconnect = () => {
    if (socketRef.current) {
      socketRef.current.onopen = null;
      socketRef.current.onclose = null;
      socketRef.current.onerror = null;
      socketRef.current.onmessage = null;
      socketRef.current.close();
      socketRef.current = null;
    }
    setIsConnected(false);
    setIsConnecting(false);
  };

  return {
    subscribe,
    unsubscribe,
    isConnected,
    isConnecting,
    getSocket: () => socketRef.current,
    addGlobalMessageListener,
    removeGlobalMessageListener,
    connect: connectWebSocket,
    disconnect,
    waitForSocketOpen,
  };
};
